// src/features/payments/components/PaymentsPage.tsx
//
// «Cobros» (mockup 08) — route /payments, Dirección. Not visible to
// Receptionist (resource-level gate); money totals stay hidden for that role
// everywhere else too. `KpiHero` tiles allowed here (a dashboard).
//
// «Cobrado» (P03) = firm charges minus refunds, every channel, no
// pre-authorisations, per currency: amounts of two currencies are shown side
// by side, never added up, and an amount the API could not measure is «—».
import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useDataProvider, useTranslate } from 'react-admin';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { HotelScopeSelector } from '@/components/common/HotelScopeSelector';
import { PeriodPicker } from '@/components/common/PeriodPicker';
import { useAnalyticsPeriod } from '@/providers/analyticsPeriodProvider';
import { KpiHero } from '@/components/common/KpiHero';
import { WidgetCard } from '@/components/common/WidgetCard';
import { ResponsiveMetricsTable, type MetricsColumn } from '@/components/common/ResponsiveMetricsTable';
import { useHotelScope } from '@/hooks/useHotelScope';
import { useAnalyticsFetch } from '@/hooks/useAnalyticsFetch';
import { useCsvExport } from '@/hooks/useCsvExport';
import { hasResourcePermission } from '@/config/resourceConfig';
import { getChannelShortLabelKey } from '@/design/registry/channelRegistry';
import { formatCount, formatDelta, formatMoney, formatMoneyFigure, formatPct } from '@/lib/analyticsFormat';
import type { PaymentsByChannel, PaymentsByHotel, PaymentsByMethod, PaymentsResponse } from '@/types/analytics';
import { paymentsArrivalsDetailKey } from '@/lib/arrivalsSource';

const t = (key: string) => `resources.payments.${key}`;

export const PaymentsPage: React.FC = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const scope = useHotelScope();
  const exportCsv = useCsvExport({ page: 'payments' });
  const canExport = hasResourcePermission('payments', 'export');

  const [period, setPeriod] = useAnalyticsPeriod();
  const [axis, setAxis] = useState<'arrival' | 'payment'>('arrival');
  const { data, loading, error, refresh } = useAnalyticsFetch<PaymentsResponse>(
    () => (dataProvider as any).getAnalyticsPayments({ ...scope.scopeParams, from: period.from, to: period.to, axis }),
    [dataProvider, scope.scopeParams, period.from, period.to, axis]
  );

  const channelLabel = (channel: string) =>
    channel === 'none' ? translate(t('byChannel.noChannel')) : translate(getChannelShortLabelKey(channel));
  const money = (amount: number | null, currency: string) => formatMoney(amount, currency);

  // Every visible column of the per-property table (D57), with its currency.
  const handleExport = async () => {
    if (!data) return;
    await exportCsv(
      data.byHotel,
      [
        { labelKey: t('byHotel.columns.hotel'), value: (r: PaymentsByHotel) => r.name },
        { labelKey: t('byHotel.columns.arrivals'), value: (r: PaymentsByHotel) => r.arrivals },
        { labelKey: t('byHotel.columns.reservations'), value: (r: PaymentsByHotel) => r.reservations },
        { labelKey: t('byHotel.columns.pct'), value: (r: PaymentsByHotel) => formatPct(r.pct) },
        { labelKey: t('byHotel.columns.amount'), value: (r: PaymentsByHotel) => r.amount },
        { labelKey: t('byHotel.columns.currency'), value: (r: PaymentsByHotel) => r.currency },
        { labelKey: t('byHotel.columns.average'), value: (r: PaymentsByHotel) => r.average },
        { labelKey: t('byHotel.columns.previousAmount'), value: (r: PaymentsByHotel) => r.previousAmount },
      ],
      { from: period.from, to: period.to }
    );
  };

  const byChannelColumns: MetricsColumn<PaymentsByChannel>[] = [
    { key: 'channel', label: translate(t('byChannel.columns.channel')), render: (row) => channelLabel(row.channel) },
    { key: 'reservations', label: translate(t('byChannel.columns.reservations')), align: 'right', render: (row) => formatCount(row.reservations) },
    { key: 'amount', label: translate(t('byChannel.columns.amount')), align: 'right', render: (row) => money(row.amount, row.currency) },
    { key: 'pct', label: translate(t('byChannel.columns.pct')), align: 'right', render: (row) => formatPct(row.pct) },
  ];

  const byMethodColumns: MetricsColumn<PaymentsByMethod>[] = [
    { key: 'method', label: translate(t('byMethod.columns.method')), render: (row) => translate(t(`byMethod.methods.${row.method}`)) },
    { key: 'reservations', label: translate(t('byMethod.columns.reservations')), align: 'right', render: (row) => formatCount(row.reservations) },
    { key: 'amount', label: translate(t('byMethod.columns.amount')), align: 'right', render: (row) => money(row.amount, row.currency) },
    { key: 'failed', label: translate(t('byMethod.columns.failed')), align: 'right', render: (row) => formatCount(row.failed) },
  ];

  const tiles = data?.tiles;
  const measurable = data?.measurable ?? true;
  const mixedCurrencies = Boolean(tiles && tiles.amount === null && tiles.byCurrency && tiles.byCurrency.length > 1);
  const refunded = tiles?.byCurrency?.filter((c) => c.refunded > 0) ?? [];
  const amountDelta =
    tiles && tiles.amount !== null && tiles.previousAmount !== null ? formatDelta(tiles.amount, tiles.previousAmount) : undefined;

  const amountNote = (() => {
    if (!tiles) return undefined;
    if (!measurable) return translate(t('tiles.notMeasurable'));
    const lines: string[] = [];
    if (tiles.selfServiceByCurrency) {
      lines.push(
        translate(t('tiles.selfServiceDetail'), {
          amount: formatMoneyFigure(tiles.selfServiceAmount, tiles.currency, tiles.selfServiceByCurrency),
        })
      );
    }
    if (tiles.refunds > 0 && refunded.length > 0) {
      lines.push(
        translate(t('tiles.refundsDetail'), {
          smart_count: tiles.refunds,
          count: formatCount(tiles.refunds),
          amount: refunded.map((c) => formatMoney(c.refunded, c.currency)).join(' · '),
        })
      );
    }
    if (tiles.preAuthorizations > 0) {
      lines.push(translate(t('tiles.preAuthorizations'), { count: formatCount(tiles.preAuthorizations) }));
    }
    if (mixedCurrencies) lines.push(translate(t('tiles.mixedCurrencies')));
    return lines.join(' · ') || undefined;
  })();

  return (
    <Box>
      <SectionHeader manualScreen="payments"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <HotelScopeSelector scope={scope} />
            <PeriodPicker value={period} onChange={setPeriod} showCompare={false} />
            <ToggleButtonGroup size="small" exclusive value={axis} onChange={(_, v) => v && setAxis(v)}>
              <ToggleButton value="arrival" sx={{ textTransform: 'none', fontSize: 12, px: 1.25 }}>
                {translate(t('axis.arrival'))}
              </ToggleButton>
              <ToggleButton value="payment" sx={{ textTransform: 'none', fontSize: 12, px: 1.25 }}>
                {translate(t('axis.payment'))}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        }
        subtitle={translate(t('subtitle'))}
        helpConfig={{
          titleKey: t('help.title'),
          descriptionKey: t('help.description'),
          featureKeys: [t('help.features.axis'), t('help.features.arrivals'), t('help.features.methods')],
          tipKeys: [t('help.tips.hidden')],
        }}
        showExport={canExport}
        onExportClick={handleExport}
      />

      {error && <FetchErrorBanner onRetry={refresh} />}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2.5 }}>
        <KpiHero
          label={translate(t('tiles.amount'))}
          value={tiles ? formatMoneyFigure(tiles.amount, tiles.currency, tiles.byCurrency) : formatMoney(undefined)}
          delta={amountDelta}
          sublabel={
            tiles
              ? tiles.pctOfArrivals !== null
                ? translate(t(paymentsArrivalsDetailKey(tiles.arrivalsSource)), {
                    count: formatCount(tiles.reservations),
                    pct: formatPct(tiles.pctOfArrivals),
                    smart_count: tiles.reservations,
                  })
                : translate(t('tiles.amountDetailNoPct'), { count: formatCount(tiles.reservations), smart_count: tiles.reservations })
              : undefined
          }
          note={amountNote}
          loading={loading}
        />
        <KpiHero label={translate(t('tiles.average'))} value={formatMoney(tiles?.average, tiles?.currency ?? 'EUR')} loading={loading} />
        <KpiHero label={translate(t('tiles.median'))} value={formatMoney(tiles?.median, tiles?.currency ?? 'EUR')} loading={loading} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
        <WidgetCard title={translate(t('byChannel.title'))}>
          <ResponsiveMetricsTable<PaymentsByChannel>
            columns={byChannelColumns}
            rows={data?.byChannel ?? []}
            getRowKey={(row) => `${row.channel}-${row.currency}`}
            loading={loading}
          />
        </WidgetCard>

        <WidgetCard title={translate(t('byMethod.title'))} description={translate(t('byMethod.description'))}>
          <ResponsiveMetricsTable<PaymentsByMethod>
            columns={byMethodColumns}
            rows={data?.byMethod ?? []}
            getRowKey={(row) => `${row.method}-${row.currency}`}
            loading={loading}
          />
        </WidgetCard>
      </Box>

      <WidgetCard title={translate(t('byHotel.title'))}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate(t('byHotel.columns.hotel'))}</TableCell>
                <TableCell align="right">{translate(t('byHotel.columns.arrivals'))}</TableCell>
                <TableCell align="right">{translate(t('byHotel.columns.reservations'))}</TableCell>
                <TableCell align="right">{translate(t('byHotel.columns.pct'))}</TableCell>
                <TableCell align="right">{translate(t('byHotel.columns.amount'))}</TableCell>
                <TableCell align="right">{translate(t('byHotel.columns.average'))}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.byHotel ?? []).map((row) => {
                const delta = row.amount !== null && row.previousAmount !== null ? formatDelta(row.amount, row.previousAmount) : undefined;
                return (
                  <TableRow key={`${row.hotelId}-${row.currency}`}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell align="right">{formatCount(row.arrivals)}</TableCell>
                    <TableCell align="right">{formatCount(row.reservations)}</TableCell>
                    <TableCell align="right">{formatPct(row.pct)}</TableCell>
                    <TableCell align="right">
                      {money(row.amount, row.currency)}
                      {delta && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                          {delta.text}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{money(row.average, row.currency)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </WidgetCard>
    </Box>
  );
};

export default PaymentsPage;
